import { Check, CircleAlert, Info, TriangleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useToastStore, type Toast, type ToastVariant } from '@/shared/stores/toast.store';

import { cn } from './cn';

/** Per-variant icon + colour. The card itself is a uniform white surface (per Figma). */
const VARIANTS: Record<ToastVariant, { icon: LucideIcon; iconColor: string }> = {
  success: { icon: Check, iconColor: 'text-green-600' },
  error: { icon: CircleAlert, iconColor: 'text-red-600' },
  warning: { icon: TriangleAlert, iconColor: 'text-amber-500' },
  info: { icon: Info, iconColor: 'text-blue-600' },
  default: { icon: Info, iconColor: 'text-foreground' },
};

const DEFAULT_DURATION = 4500;

function ToastCard({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismissToast);
  const { icon: Icon, iconColor } = VARIANTS[toast.variant];
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => {
      dismiss(toast.id);
    }, toast.duration ?? DEFAULT_DURATION);
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, toast.duration, dismiss, paused]);

  return (
    <div
      role="status"
      onMouseEnter={() => {
        setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
      onFocus={() => {
        setPaused(true);
      }}
      onBlur={() => {
        setPaused(false);
      }}
      className="pointer-events-auto flex w-fit max-w-md items-center gap-3 rounded-[10px] border border-border bg-background p-4 shadow-sm animate-in fade-in-0 slide-in-from-top-2"
    >
      <Icon className={cn('size-6 shrink-0', iconColor)} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-sm text-muted-foreground">{toast.description}</p>
        )}
      </div>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick();
            dismiss(toast.id);
          }}
          className="shrink-0 rounded-md bg-secondary px-2 py-1 text-xs text-secondary-foreground transition-colors hover:bg-secondary/80"
        >
          {toast.action.label}
        </button>
      )}
    </div>
  );
}

/** App-wide toast renderer — top-centered. Subscribes to the shared toast store. */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed left-1/2 top-4 z-[100] flex -translate-x-1/2 flex-col items-center gap-2">
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
